import { AccountBalanceWallet, Person, Visibility } from '@mui/icons-material';
import { Box, Chip, Tooltip } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { InvestmentAllocation } from '../../models/InvestmentEntity';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { StyledIconButton } from '../styles/StyledButtons';
import { StyledAvatar, StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';
import { getOpportunityId } from './entityManagementUtils';

interface EntityManagementAllocationCardProps {
  allocation: InvestmentAllocation;
}

/** Compact allocation card for entity management detail view */
export const EntityManagementAllocationCard: React.FC<EntityManagementAllocationCardProps> = ({
  allocation,
}) => {
  const navigate = useNavigate();

  const handleViewOpportunity = () => {
    const opportunityId = getOpportunityId(allocation);

    if (!opportunityId) {
      alert('Unable to find opportunity ID. Check console for details.');
      return;
    }

    navigate(`/opportunities/${opportunityId}?investorId=${allocation.investorId}`);
  };

  const handleViewInvestor = () => {
    const opportunityId = getOpportunityId(allocation);

    if (opportunityId) {
      navigate(`/opportunities/${opportunityId}/investors/${allocation.investorId}`);
    } else {
      alert('Unable to find opportunity ID for investor view. Check console for details.');
    }
  };

  return (
    <Box
      sx={{
        flex: '1 1 300px',
        minWidth: 260,
        maxWidth: 360,
      }}
    >
      <StyledCard tone="default" sx={{ mb: 1, height: '100%' }}>
        <StyledCardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <StyledAvatar tone="tone1" sx={{ width: 28, height: 28 }}>
              <AccountBalanceWallet sx={{ fontSize: 16 }} />
            </StyledAvatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <StyledTypography
                  fontWeight={600}
                  fontSize="0.875rem"
                  sx={{
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                  title={allocation.fundOpportunity.name || ''}
                >
                  {allocation.fundOpportunity.name || 'Unnamed Opportunity'}
                </StyledTypography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                  mt: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={allocation.fundOpportunity.status}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {allocation.fundOpportunity.country && (
                  <Chip
                    label={allocation.fundOpportunity.country}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>

              <Box sx={{ mt: 0.75 }}>
                <StyledTypography fontWeight={600} sx={{ color: 'primary.main' }}>
                  {ExtendedLiquid.currencyMillions(Number(allocation.allocatedAmount), false)}
                </StyledTypography>
                {allocation.fundOpportunity.summaryTitle && (
                  <StyledTypography
                    fontSize="0.75rem"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      mt: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={allocation.fundOpportunity.summaryTitle}
                  >
                    {allocation.fundOpportunity.summaryTitle}
                  </StyledTypography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="View Opportunity">
                <StyledIconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewOpportunity();
                  }}
                >
                  <Visibility fontSize="small" />
                </StyledIconButton>
              </Tooltip>
              <Tooltip title="View Investor">
                <StyledIconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewInvestor();
                  }}
                >
                  <Person fontSize="small" />
                </StyledIconButton>
              </Tooltip>
            </Box>
          </Box>
        </StyledCardContent>
      </StyledCard>
    </Box>
  );
};
